'use client';

import { Subsidiary } from '@coolblue-development/constants';
import {
  Box,
  Button,
  Flex,
  freddyTheme,
  Modal,
  ModalBody,
  ModalFooter,
  Typography
} from '@coolblue-development/freddy';
import closeCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/closeCollectionTrigger';
import confirmTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/confirmTrigger';
import openCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/openCollectionTrigger';
import showCollectionTrigger from '@coolblue-development/ga4-events/triggers/ProductCollection/showCollectionTrigger';
import { ArrowRight, CheckmarkCircled } from '@coolblue-development/icons';
import { TrackImmediate, useAnalytics } from '@coolblue-development/next-analytics/client';
import { AvailabilityState as ClientAvailabilityState } from '@coolblue-development/next-availability-state/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { useRequestContext } from '@coolblue-development/next-request-context/client';
import {
  SalesPrice,
  SalesPriceCurrent,
  SalesPriceFormer
} from '@coolblue-development/next-sales-price/client';
import { AvailabilityState } from '@features/product/typings';
import { useGetCollectionTitle } from '@hooks/collectionHooks';
import { useBreakpoints } from '@hooks/useBreakpoints';
import { useTranslation } from '@i18n/useTranslation';
import {
  getCurrentProductValues,
  matchPropertyValues,
  shouldShowCollection
} from '@utils/collectionUtils';
import { useRouter } from 'next/navigation';
import { Fragment, useState } from 'react';

import { MobileEntrance } from './Collection.styled';
import {
  type CollectionProps,
  CollectionType,
  type Price,
  type Product,
  Variant
} from './Collection.types';
import { DropdownCollectionMobile } from './DropdownCollection/DropdownCollection.Mobile';
import { PropertyCollection } from './PropertyCollection';
import { CollectionTitle } from './Shared/CollectionTitle';

const VALUE_SEPARATOR = '|';

export const MobileCollection = ({ collection, mainImageId, product }: CollectionProps) => {
  const router = useRouter();
  const { trackEvent } = useAnalytics();
  const modalTranslations = useFreddyTranslations('modal');
  const { isMobile } = useBreakpoints();
  const { t } = useTranslation('webshop.productPage');
  const { isInternalUser, subsidiary } = useRequestContext();

  const getListPriceForProduct = (
    listPrice: Price | null | undefined,
    prices?: { msrp?: Price }
  ) => {
    return subsidiary === Subsidiary.DE && prices?.msrp?.includingVat ? prices.msrp : listPrice;
  };

  const initialSelectedProduct = {
    availabilityState: product.availabilityState,
    price: product.salesPrice.includingVat,
    listPrice: getListPriceForProduct(product?.listPrice, product?.prices)?.includingVat,
    redirectUrl: '',
    id: product.id
  };

  const [isOpen, setIsOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(initialSelectedProduct);

  const handleProductClick = (product: Product | null) => {
    if (product && product.nullableAvailabilityState && product.nullableSalesPrice) {
      setSelectedProduct({
        availabilityState: product.nullableAvailabilityState,
        price: product.nullableSalesPrice.includingVat,
        listPrice: getListPriceForProduct(product?.listPrice, product?.prices)?.includingVat,
        redirectUrl: product.urls.product || '',
        id: product.id || 0
      });
    }
  };

  const collectionTitle = useGetCollectionTitle(collection.title, product.productType);

  const mappedCollection = matchPropertyValues(collection, selectedProduct?.id);

  if (!shouldShowCollection(mappedCollection)) return <></>;

  const collectionProductIds = mappedCollection.discriminants[0].values
    .flatMap(value => value.products)
    .filter(product => product?.id)
    .map(product => product?.id);

  const getAvailabilityStateProps = (availabilityState: string) => {
    switch (availabilityState) {
      case AvailabilityState.OnStock:
        return {
          promise: t('onStock'),
          state: {
            color: freddyTheme.color.font.sentimentPositiveContrast,
            icon: <CheckmarkCircled color={freddyTheme.color.icon.sentimentPositiveContrast} />
          }
        };
      case AvailabilityState.AvailableSoon:
        return {
          promise: t('availableSoon'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityState.PreOrder:
        return {
          promise: t('preOrder'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      case AvailabilityState.OutOfStock:
        return {
          promise: t('permanentlyOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
      default:
        return {
          promise: t('temporaryOutOfStock'),
          state: {
            color: freddyTheme.color.font.sentimentNegative
          }
        };
    }
  };

  const EntranceValues = () => {
    const currentValues = getCurrentProductValues(collection);
    const totalValues = currentValues.length;

    return currentValues.map((currentValue, index) => (
      <Fragment key={index}>
        {currentValue && (
          <>
            <Typography
              m={0}
              sx={{
                whiteSpace: 'nowrap',
                ...(index === totalValues - 1 && {
                  overflow: 'hidden',
                  textOverflow: 'ellipsis'
                })
              }}
            >
              {currentValue}
            </Typography>
            {index < totalValues - 1 && (
              <Typography m={0} color='additional'>
                {VALUE_SEPARATOR}
              </Typography>
            )}
          </>
        )}
      </Fragment>
    ));
  };

  const handleConfirmButtonClick = () => {
    setIsOpen(false);

    const shouldRedirect =
      selectedProduct.redirectUrl &&
      selectedProduct.redirectUrl !== initialSelectedProduct.redirectUrl;
    if (shouldRedirect) {
      router.push(selectedProduct.redirectUrl);
    }

    trackEvent(
      confirmTrigger({
        sub_feature:
          mappedCollection.type === CollectionType.DROPDOWN
            ? 'drop_down_selector'
            : 'multi_discriminant_selector',
        index: 1,
        total_indices: 1,
        item_id: selectedProduct.id
      })
    );
  };

  const handleOpenModal = () => {
    setIsOpen(true);
    trackEvent(
      openCollectionTrigger({
        index: 1,
        item_list_id: product?.id ?? 0,
        item_list_name: product?.name ?? '',
        number_of_items: collectionProductIds.length,
        total_indices: 1,
        sub_feature:
          mappedCollection.type === CollectionType.DROPDOWN
            ? 'drop_down_selector'
            : 'multi_discriminant_selector'
      })
    );
  };

  return (
    <Box hideFrom='md' {...(isInternalUser && { 'data-cy': 'mobile-collection' })}>
      {isMobile && (
        <TrackImmediate
          trigger={showCollectionTrigger({
            sub_feature:
              mappedCollection.type === CollectionType.DROPDOWN
                ? 'drop_down_selector'
                : 'multi_discriminant_selector',
            item_ids: collectionProductIds.toString()
          })}
        />
      )}
      <MobileEntrance
        {...(isInternalUser && { 'data-cy': 'mobile-entrance' })}
        onClick={() => handleOpenModal()}
        justifyContent='space-between'
        alignItems='center'
      >
        <Flex gap={100} sx={{ overflow: 'hidden' }}>
          <CollectionTitle
            title={collectionTitle}
            productType={product.productType}
            variant='entrance'
          />
          <EntranceValues />
        </Flex>
        <ArrowRight color={freddyTheme.color.icon.interactiveDisabled} />
      </MobileEntrance>
      <Modal
        {...(isInternalUser && { 'data-cy': 'modal-collection' })}
        translations={modalTranslations}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          setSelectedProduct(initialSelectedProduct); // Reset values when closed
          trackEvent(
            closeCollectionTrigger({
              sub_feature:
                mappedCollection.type === CollectionType.DROPDOWN
                  ? 'drop_down_selector'
                  : 'multi_discriminant_selector'
            })
          );
        }}
        placement='bottom'
        title={collectionTitle}
        size='xlarge'
        body={
          <ModalBody>
            {collection.type === CollectionType.DROPDOWN ? (
              <DropdownCollectionMobile
                collection={mappedCollection}
                onProductClick={handleProductClick}
                selectedProductId={selectedProduct?.id}
              />
            ) : (
              <PropertyCollection
                collection={mappedCollection}
                mainImageId={mainImageId}
                onProductClick={handleProductClick}
                product={product}
                variant={Variant.MOBILE}
                selectedProductId={selectedProduct?.id}
              />
            )}
          </ModalBody>
        }
        footer={
          <ModalFooter>
            <Flex justifyContent='space-between' gap={200} sx={{ width: '100%' }}>
              <Flex sx={{ width: '50%' }} direction='column' gap={200}>
                {selectedProduct.price && (
                  <SalesPrice
                    currentPrice={<SalesPriceCurrent price={selectedProduct.price} />}
                    {...(selectedProduct.listPrice && {
                      formerPrice: (
                        <SalesPriceFormer
                          price={selectedProduct.listPrice}
                          prefix={t('salesPrice.prefix')}
                        />
                      )
                    })}
                  />
                )}
                {selectedProduct.availabilityState && (
                  <ClientAvailabilityState
                    {...getAvailabilityStateProps(selectedProduct.availabilityState)}
                  />
                )}
              </Flex>
              <Button sx={{ width: '50%' }} onClick={handleConfirmButtonClick}>
                {t('collection.confirmButton')}
              </Button>
            </Flex>
          </ModalFooter>
        }
      />
    </Box>
  );
};
